import { read_cookie } from "sfcookies";
import { hostUrl, UseUrlManagerCleanUrl } from "../components/utils/url-manager";

/**
 * How every HQ panel reaches an Operator.
 *
 * HQAPI-76. These panels used to read `host` and `token` straight off the Operator record that
 * `GET /hq_api/operators` returned, and `token` is the Operator's own `TOKEN` environment
 * variable: one string that opens every route on that Operator, with no expiry. Six of the nine
 * callers put it in a **websocket URL's query string**, which nginx writes to its access log and
 * the browser keeps in history, and three sent it as a header on `fetch()` calls made from the
 * browser straight to the Operator host.
 *
 * HQ-API does not serve it any more. A panel asks here instead and gets a host and a ticket that
 * is signed, bound to the asking admin and the Operator it names, and good for about a minute.
 *
 * The same shape Daive's chat drawer has used since its own rebuild - see
 * `ChatBotContextProvider.loadChatConnection`, which is where the pattern and the subprotocol
 * trick below both come from.
 */
export type OperatorConnection = {
    id: number,
    host: string,
    ticket: string,
    expiresIn: number,
};

/**
 * Ask HQ-API how to reach one Operator.
 *
 * **Call this for every connection, including every reconnection.** A ticket is spent when it
 * opens a socket and it expires about a minute after it is issued, so a stored one is refused as
 * a replay rather than merely being stale.
 *
 * @param operatorId the Operator being connected to. It travels inside the signature, so a
 *                   ticket for one cannot be pointed at another.
 */
export async function fetchOperatorConnection(operatorId: number | string): Promise<OperatorConnection> {
    const url: string = UseUrlManagerCleanUrl({
        url: hostUrl + 'hq/operator/' + String(operatorId) + '/connection'
    });

    const response: Response = await fetch(new Request(url, {
        method: 'GET',
        headers: new Headers({
            'Content-Type': 'application/json',
            'token': read_cookie('auth') as unknown as string,
        }),
    }));

    if (!response.ok) {
        const body: { message?: string } = await response.json().catch((): object => ({}));

        throw new Error(body.message ?? 'Unable to reach that operator right now.');
    }

    return await response.json() as OperatorConnection;
}

/**
 * The websocket URL for one path on an Operator, with the ticket carried in the subprotocol.
 *
 * **The ticket is deliberately not in the query string**, which is the other half of what
 * HQAPI-76 was about: a query string is written to nginx's access log, kept in browser history
 * and passed on by any proxy in between, so a credential there outlives the request it was for.
 * The browser sends a subprotocol in the `Sec-WebSocket-Protocol` header instead, which is not
 * logged as part of the URL. This is the same arrangement Daive's chat socket uses.
 *
 * The caller passes the subprotocols to `new WebSocket(url, protocols)`; the URL alone is not
 * enough.
 */
export function operatorSocketUrl(
    connection: OperatorConnection,
    path: string,
    params: Record<string, string> = {}
): string {
    // Hosts have always been stored with a trailing slash on some rows, which would otherwise
    // produce `wss://host//operator/...`.
    const host: string = connection.host
        .replace('https://', 'wss://')
        .replace('http://', 'ws://')
        .replace(/\/+$/, '');

    const query: string = new URLSearchParams(params).toString();

    return host + path + (query === '' ? '' : '?' + query);
}

/**
 * The subprotocols to open an Operator socket with.
 *
 * Two of them: the protocol version, so the Operator can tell one generation of client from the
 * next, and the ticket. A subprotocol token cannot contain characters outside a narrow set, which
 * is why the ticket is base64url rather than base64.
 */
export function operatorSocketProtocols(connection: OperatorConnection): string[] {
    return ['operator.v1', 'operator.ticket.' + connection.ticket];
}

/**
 * The headers for a `fetch()` a panel makes to the Operator host directly.
 *
 * The status tile, the log viewer, the connection count and the Email Finder activity panel all
 * do this. They sent the standing token here; they send a ticket now, in the same header, which
 * is why `VerifyRequest` on the Operator accepts either - HQ-API's own server-to-server dispatch
 * still uses the standing one and never passes through a browser.
 */
export function operatorRequestHeaders(connection: OperatorConnection): Headers {
    return new Headers({
        'Content-Type': 'application/json',
        'token': connection.ticket,
    });
}
